import React, { useContext, useEffect } from 'react';
import { useVirtual } from 'react-virtual';
import { Context } from '@front10/helpers/dist/Context';
import LocationSelectorGroupHeader from './LocationSelectorGroupHeader';
import LocationSelectorMenuItem from './LocationSelectorMenuItem';

const RowVirtualizerDynamic = ({
  items,
  dropDownHeight,
  size,
  itemsProps,
  grouped,
  displayField,
  id,
  itemHeight,
  highlightedIndex,
  itemRenderer,
  getItemProps,
  customAccessibility,
  enableVirtualList,
  getMenuProps,
  wasOpenFirstTime,
  useInput,
  openOnFocus
}) => {
  const parentRef = React.useRef();
  const { translate } = useContext(Context);
  const rowVirtualizer = useVirtual({
    size,
    parentRef,
    overscan: 2
  });

  const menuProps = getMenuProps();
  const newMenuProps = {
    'aria-labelledby': null,
    role: !useInput ? null : menuProps['role']
  };

  // Scroll virtualizer to currently highlighted item
  useEffect(() => {
    if (highlightedIndex !== undefined) {
      rowVirtualizer.scrollToIndex(highlightedIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedIndex]);

  return (
    <div className="fr-dropdown-list-container" tabIndex="-1">
      <ol
        role="listbox"
        className="fr-px-0 fr-ol"
        {...getMenuProps({
          ref: parentRef,
          style: {
            margin: 0,
            padding: 0,
            position: 'relative',
            overflowY: 'auto',
            maxHeight: dropDownHeight,
            width: '100%'
          }
        })}
        {...newMenuProps}
        id={`${menuProps['id']}-list`}
      >
        <div
          style={{
            height: `${rowVirtualizer.totalSize}px`,
            width: '100%',
            position: 'relative'
          }}
        >
          {rowVirtualizer.virtualItems.map((virtualRow) => {
            const item = items[virtualRow.index];
            const Tag = grouped && item.category ? 'div' : 'li';

            const locationItemProps = itemsProps(
              virtualRow.index,
              highlightedIndex,
              items,
              getItemProps
            );

            const result = (
              <Tag
                {...locationItemProps.attrs}
                {...locationItemProps.itemProps}
                //{...locationItemProps.props}
                key={items[virtualRow.index].value}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  marginTop: 0,
                  marginBottom: 0,
                  minHeight: `${itemHeight}px`,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`
                }}
                ref={virtualRow.measureRef}
                aria-selected={useInput ? locationItemProps.props['aria-selected'] : null}
                role={useInput ? 'option' : null}
                aria-posinset={virtualRow.index + 1}
                aria-setsize={size}
              >
                {grouped && item.category ? (
                  <LocationSelectorGroupHeader {...locationItemProps.props}>
                    {itemRenderer(item)}
                  </LocationSelectorGroupHeader>
                ) : (
                  <LocationSelectorMenuItem
                    {...locationItemProps.props}
                    id={id ? `${id}-item-${virtualRow.index}` : undefined}
                    ariaLabel={
                      customAccessibility
                        ? `${item[displayField]}  ${virtualRow.index + 1} ${translate(
                            'cmp-location-selector-separator',
                            'of'
                          )} ${size}`
                        : item[displayField]
                    }
                    customAccessibility={customAccessibility}
                    wasOpenFirstTime={wasOpenFirstTime}
                    openOnFocus={openOnFocus}
                    useInput={useInput}
                  >
                    {itemRenderer(item)}
                  </LocationSelectorMenuItem>
                )}
              </Tag>
            );
            return !size || (size && virtualRow.index < size) ? result : null;
          })}
        </div>
      </ol>
    </div>
  );
};

export default RowVirtualizerDynamic;
